import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable, Linking, Platform, Image } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import { fontStyle } from '../config/fonts';
import YouTubeWebViewModal from './YouTubeWebViewModal';

interface LinkEmbedProps {
  url: string;
  style?: any;
}

export default function LinkEmbed({ url, style }: LinkEmbedProps) {
  const { colors, themeMode } = useTheme();
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);

  // Helper to extract YouTube video ID
  const getYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&]+)/,
      /(?:youtu\.be\/)([^?]+)/,
      /(?:youtube\.com\/embed\/)([^?]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  // Helper to get Twitter/X tweet ID
  const getTwitterId = (url: string): string | null => {
    const match = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
    return match ? match[1] : null;
  };

  // Determine link type
  const youtubeId = getYouTubeId(url);
  const twitterId = getTwitterId(url);

  const handleOpenLink = () => {
    Linking.openURL(url);
  };

  const handleOpenYouTube = () => {
    setShowYouTubeModal(true);
  };

  // YouTube embed
  if (youtubeId) {
    const thumbnailUrl = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

    // On web, use iframe
    if (Platform.OS === 'web') {
      const embedUrl = `https://www.youtube.com/embed/${youtubeId}`;
      return (
        <View style={[styles.container, style]}>
          <iframe
            src={embedUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </View>
      );
    }

    // On native, show clickable thumbnail that opens in-app WebView modal
    return (
      <>
        <Pressable onPress={handleOpenYouTube}>
          <View style={[styles.youtubePreview, style]}>
            <Image
              source={{ uri: thumbnailUrl }}
              style={styles.youtubeThumbnail}
              resizeMode="cover"
            />
            <View style={styles.youtubeOverlay}>
              <View style={styles.youtubePlayButton}>
                <Feather name="play" size={32} color="#fff" />
              </View>
            </View>
            <View style={[styles.youtubeLabel, { backgroundColor: 'rgba(0,0,0,0.8)' }]}>
              <Feather name="youtube" size={16} color="#FF0000" />
              <Text style={[styles.youtubeLabelText, fontStyle, { color: '#fff' }]}>
                Watch on YouTube
              </Text>
            </View>
          </View>
        </Pressable>
        <YouTubeWebViewModal
          visible={showYouTubeModal}
          videoId={youtubeId}
          onClose={() => setShowYouTubeModal(false)}
        />
      </>
    );
  }

  // Twitter/X embed
  if (twitterId) {
    // For Twitter, just show a link with option to open
    // Twitter embeds are complex and require their widget script
    return (
      <Pressable onPress={handleOpenLink}>
        <View style={[styles.twitterLink, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <View style={styles.twitterHeader}>
            <Text style={[styles.twitterIcon, fontStyle]}>𝕏</Text>
            <Text style={[styles.twitterLabel, fontStyle, { color: colors.textSecondary }]}>
              X (Twitter) Post
            </Text>
          </View>
          <Text style={[styles.linkText, fontStyle, { color: colors.text }]} numberOfLines={1}>
            {url}
          </Text>
          <Feather name="external-link" size={14} color={colors.textSecondary} />
        </View>
      </Pressable>
    );
  }

  // Generic link fallback
  return (
    <Pressable onPress={handleOpenLink}>
      <View style={[styles.genericLink, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <Feather name="link" size={16} color={colors.primary} />
        <Text style={[styles.linkText, fontStyle, { color: colors.text }]} numberOfLines={1}>
          {url}
        </Text>
        <Feather name="external-link" size={14} color={colors.textSecondary} />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
    borderRadius: 8,
    overflow: 'hidden',
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  youtubePreview: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#000',
  },
  youtubeThumbnail: {
    width: '100%',
    height: '100%',
  },
  youtubeOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  youtubePlayButton: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(255,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  youtubeLabel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    gap: 6,
  },
  youtubeLabelText: {
    fontSize: 12,
    fontWeight: '600',
  },
  twitterLink: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  twitterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  twitterIcon: {
    fontSize: 16,
    fontWeight: '600',
  },
  twitterLabel: {
    fontSize: 12,
    fontWeight: '500',
  },
  genericLink: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  linkText: {
    flex: 1,
    fontSize: 13,
  },
});
